import { CopilotActions } from 'actions/actionTypes';

function newOmnisearchRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `omni-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Stable key for {@link state.copilot.contextOmnisearchCache}. Matches RECEIVE and hydrate lookups.
 * @param {string} [query]
 * @param {string | null} [searchType] `null` / omit encodes global @-mention (no `search_type`).
 * @returns {string}
 */
export function buildOmnisearchCacheKey(query, searchType) {
  const q = String(query ?? '').trim();
  const st =
    searchType != null && String(searchType).trim() !== ''
      ? String(searchType).trim()
      : null;
  return `${st ?? 'global'}::${q}`;
}

/**
 * Omnisearch WS payloads may echo `request_id` (snake_case) while the client tracks `requestId`.
 * Without this, {@link CopilotActions.RECEIVE_COPILOT_OMNISEARCH} cannot match the active slot and
 * stale responses are not dropped.
 * @param {Record<string, unknown> | null | undefined} data
 * @returns {string | null}
 */
export function readCopilotOmnisearchRequestIdFromWsData(data) {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const id = data.requestId ?? data.request_id;
  if (id == null) {
    return null;
  }
  return String(id);
}

/**
 * @-mention omnisearch: WebSocket `message_type: omnisearch` (see `helpers/websockets.js` send).
 * Optional `request_id` in the payload/response is used to drop stale responses.
 *
 * When a matching entry exists in `contextOmnisearchCache`, dispatches
 * `HYDRATE_COPILOT_OMNISEARCH_FROM_CACHE` instead (no socket, no loading) — e.g. Add context tab switch.
 *
 * @param {string} query
 * @param {{
 *   searchType?: string,
 *   bypassCache?: boolean
 * }} [options] Optional `searchType` scopes Add context tab omnisearch; omit for global @-mention.
 */
export const sendCopilotOmnisearchQuery = (
  query,
  options = {}
) => (dispatch, getState) => {
  const { searchType, bypassCache = false } = options;
  const nextQ = String(query ?? '').trim();
  const nextSt =
    searchType != null && String(searchType).trim() !== ''
      ? String(searchType).trim()
      : null;
  const cacheKey = buildOmnisearchCacheKey(query, nextSt);
  if (!bypassCache && typeof getState === 'function') {
    const cached = getState().copilot?.contextOmnisearchCache?.[cacheKey];
    if (cached?.results != null) {
      dispatch({
        type: CopilotActions.HYDRATE_COPILOT_OMNISEARCH_FROM_CACHE,
        requestQuery: nextQ,
        requestSearchType: nextSt,
        results: cached.results
      });
      return;
    }
  }
  const requestId = newOmnisearchRequestId();
  dispatch({
    type: CopilotActions.SEND_COPILOT_OMNISEARCH,
    query: nextQ,
    requestId,
    searchType: nextSt
  });
};

/**
 * @param {object} [options]
 * @param {boolean} [options.preserveDefaultOmnisearch] when true, keep `requestQuery` and `results` so
 * default (empty query) WebSocket omnisearch can be reused on the next open without a flash of empty.
 */
export const clearCopilotOmnisearch = (options = {}) => ({
  type: CopilotActions.CLEAR_COPILOT_OMNISEARCH,
  preserveDefaultOmnisearch: Boolean(options.preserveDefaultOmnisearch)
});
